import { useState, useRef, useEffect } from 'react'
import {
  Bell, Globe, LogOut, Shield, ChevronDown, Copy, Check,
  Mail, Phone, Star, FlaskConical, MapPin, User, Loader2,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useI18n } from '@/i18n'
import { useAuthStore } from '@/stores/authStore'
import { supabase } from '@/lib/supabase'
import { usersApi } from '@/lib/api/users'
import type { UserRoleLevel } from '@/types/users'

// --- Avatar helpers ---

function getInitials(username?: string, email?: string): string {
  if (username && username.length >= 2) return username.slice(0, 2).toUpperCase()
  if (username) return username.toUpperCase()
  if (email) return email.slice(0, 2).toUpperCase()
  return 'AD'
}

const AVATAR_COLORS = [
  { bg: 'bg-violet-100', text: 'text-violet-700', ring: 'ring-violet-200' },
  { bg: 'bg-blue-100',   text: 'text-blue-700',   ring: 'ring-blue-200'   },
  { bg: 'bg-emerald-100',text: 'text-emerald-700', ring: 'ring-emerald-200'},
  { bg: 'bg-amber-100',  text: 'text-amber-700',  ring: 'ring-amber-200'  },
  { bg: 'bg-rose-100',   text: 'text-rose-700',   ring: 'ring-rose-200'   },
  { bg: 'bg-cyan-100',   text: 'text-cyan-700',   ring: 'ring-cyan-200'   },
  { bg: 'bg-indigo-100', text: 'text-indigo-700', ring: 'ring-indigo-200' },
  { bg: 'bg-teal-100',   text: 'text-teal-700',   ring: 'ring-teal-200'   },
]

function getAvatarColor(id?: string) {
  if (!id) return AVATAR_COLORS[0]
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i)
    hash |= 0
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

// --- Role badge ---

const ROLE_STYLE: Record<string, { bg: string; text: string; label: Record<string, string> }> = {
  free:    { bg: 'bg-gray-100',   text: 'text-gray-600',   label: { zh: '免费版', en: 'Free'    } },
  vip:     { bg: 'bg-yellow-100', text: 'text-yellow-700', label: { zh: 'VIP',    en: 'VIP'     } },
  premium: { bg: 'bg-purple-100', text: 'text-purple-700', label: { zh: '高级版', en: 'Premium' } },
  pro:     { bg: 'bg-blue-100',   text: 'text-blue-700',   label: { zh: 'Pro',    en: 'Pro'     } },
}

function RoleBadge({ role, lang }: { role: string; lang: string }) {
  const s = ROLE_STYLE[role as UserRoleLevel] ?? ROLE_STYLE.free
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.bg} ${s.text}`}>
      {s.label[lang] ?? role}
    </span>
  )
}

// --- Row helper ---

function InfoRow({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-xs text-gray-600">
      <span className="mt-0.5 flex-shrink-0 text-gray-400">{icon}</span>
      <span className="min-w-0 break-all leading-relaxed">{children}</span>
    </div>
  )
}

// --- Main component ---

export function Header() {
  const { language, setLanguage, messages } = useI18n()
  const nav = messages.navigation
  const user = useAuthStore((state) => state.user)
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Fetch complete profile — runs once after login, cached for 5 min
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['admin-self-profile', user?.user_id],
    queryFn: () => usersApi.getUserComplete(user!.user_id),
    enabled: !!user?.user_id,
    staleTime: 5 * 60 * 1000,
  })

  const toggleLanguage = () => setLanguage(language === 'zh' ? 'en' : 'zh')

  const handleLogout = async () => {
    setMenuOpen(false)
    await supabase.auth.signOut({ scope: 'local' })
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_user_id')
    localStorage.removeItem('admin_email')
    localStorage.removeItem('admin_username')
    sessionStorage.removeItem('admin_token')
    navigate('/login', { replace: true })
  }

  const handleCopyId = async () => {
    if (!user?.user_id) return
    try {
      await navigator.clipboard.writeText(user.user_id)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard API unavailable (non-HTTPS or unsupported browser)
    }
  }

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Derive display values — prefer richer profile data, fall back to auth store
  const displayName = profile?.display_name || profile?.nickname || user?.username || user?.email?.split('@')[0] || 'Admin'
  const username    = profile?.username    || user?.username
  const email       = profile?.email       || user?.email
  const phone       = profile?.phone_number
  const roleLevel   = profile?.role_level  || 'free'
  const isAdmin     = profile?.is_admin    ?? false  // don't show badge until profile confirms
  const isBeta      = profile?.is_beta_tester ?? false
  const realName    = profile?.real_name
  const birthDate   = profile?.birth_date
  const birthLoc    = profile?.birth_location
  const hasAstro    = profile?.has_astro_profile ?? false

  const initials = getInitials(username, email)
  const color    = getAvatarColor(user?.user_id)

  // Short user ID for display
  const shortId = user?.user_id
    ? `${user.user_id.slice(0, 8)}…${user.user_id.slice(-4)}`
    : null

  // Format birth date: ISO → YYYY-MM-DD
  const birthDateDisplay = birthDate ? birthDate.slice(0, 10) : null

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
      {/* Page Title */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800">{nav.appTitle}</h2>
      </div>

      {/* Right Side */}
      <div className="flex items-center gap-3">
        {/* Language Switcher */}
        <button
          onClick={toggleLanguage}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
          title={language === 'zh' ? 'Switch to English' : '切换到中文'}
        >
          <Globe className="w-4 h-4" />
          <span>{language === 'zh' ? '中文' : 'EN'}</span>
        </button>

        {/* Notifications */}
        <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
          <Bell className="w-5 h-5" />
        </button>

        {/* User Menu trigger */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2.5 pl-1 pr-2.5 py-1.5 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
          >
            {/* Avatar */}
            <div className={`w-8 h-8 rounded-full ${color.bg} ring-2 ${color.ring} flex items-center justify-center flex-shrink-0`}>
              <span className={`text-xs font-bold ${color.text} leading-none`}>{initials}</span>
            </div>

            {/* Name + role pill */}
            <div className="text-left hidden sm:block">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium text-gray-800 leading-tight">{displayName}</p>
                <RoleBadge role={roleLevel} lang={language} />
              </div>
              {username && username !== displayName && (
                <p className="text-xs text-gray-400 leading-tight">@{username}</p>
              )}
            </div>

            <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${menuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* ─── Dropdown ─── */}
          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">

              {/* Identity card */}
              <div className="px-4 pt-4 pb-3 bg-gradient-to-br from-gray-50 to-white border-b border-gray-100">
                <div className="flex items-start gap-3">
                  {/* Large avatar */}
                  <div className={`w-12 h-12 rounded-full ${color.bg} ring-2 ${color.ring} flex items-center justify-center flex-shrink-0`}>
                    <span className={`text-base font-bold ${color.text} leading-none`}>{initials}</span>
                  </div>

                  <div className="min-w-0 flex-1">
                    {/* Name + badges row */}
                    <div className="flex flex-wrap items-center gap-1 mb-0.5">
                      <p className="font-semibold text-gray-900 text-sm">{displayName}</p>
                      <RoleBadge role={roleLevel} lang={language} />
                      {isAdmin && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-primary-50 border border-primary-100">
                          <Shield className="w-2.5 h-2.5 text-primary-600" />
                          <span className="text-[10px] font-medium text-primary-700 leading-none">
                            {language === 'zh' ? '管理员' : 'Admin'}
                          </span>
                        </span>
                      )}
                      {isBeta && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-100">
                          <FlaskConical className="w-2.5 h-2.5 text-emerald-600" />
                          <span className="text-[10px] font-medium text-emerald-700 leading-none">
                            {language === 'zh' ? '内测' : 'Beta'}
                          </span>
                        </span>
                      )}
                    </div>

                    {/* @username */}
                    {username && (
                      <p className="text-xs text-gray-500 mb-1">@{username}</p>
                    )}

                    {/* User ID copy button */}
                    {shortId && (
                      <button
                        onClick={handleCopyId}
                        className="flex items-center gap-1 group/copy"
                        title={language === 'zh' ? '点击复制完整 ID' : 'Click to copy full ID'}
                      >
                        <span className="text-[11px] font-mono text-gray-400 group-hover/copy:text-gray-600 transition-colors">
                          {shortId}
                        </span>
                        {copied
                          ? <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                          : <Copy className="w-3 h-3 text-gray-300 group-hover/copy:text-gray-500 flex-shrink-0 transition-colors" />
                        }
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Loading state */}
              {profileLoading && (
                <div className="px-4 py-3 flex items-center gap-2 text-xs text-gray-400 border-b border-gray-100">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>{language === 'zh' ? '加载用户信息…' : 'Loading profile…'}</span>
                </div>
              )}

              {/* Contact info */}
              {!profileLoading && (email || phone) && (
                <div className="px-4 py-3 space-y-1.5 border-b border-gray-100">
                  {email && (
                    <InfoRow icon={<Mail className="w-3.5 h-3.5" />}>
                      {email}
                    </InfoRow>
                  )}
                  {phone && (
                    <InfoRow icon={<Phone className="w-3.5 h-3.5" />}>
                      {phone}
                    </InfoRow>
                  )}
                </div>
              )}

              {/* Self 盘 */}
              {!profileLoading && hasAstro && (
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                    {language === 'zh' ? '本命盘' : 'Birth Chart'}
                  </p>
                  <div className="space-y-1.5">
                    {realName && (
                      <InfoRow icon={<User className="w-3.5 h-3.5" />}>
                        <span className="font-medium text-gray-700">{realName}</span>
                      </InfoRow>
                    )}
                    {birthDateDisplay && (
                      <InfoRow icon={<Star className="w-3.5 h-3.5" />}>
                        {birthDateDisplay}
                        {profile?.birth_time && (
                          <span className="text-gray-400 ml-1">{profile.birth_time.slice(0, 5)}</span>
                        )}
                      </InfoRow>
                    )}
                    {birthLoc && (
                      <InfoRow icon={<MapPin className="w-3.5 h-3.5" />}>
                        {birthLoc}
                      </InfoRow>
                    )}
                  </div>
                </div>
              )}

              {/* No astro profile hint */}
              {!profileLoading && !hasAstro && profile && (
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs text-gray-400 italic">
                    {language === 'zh' ? '暂无本命盘信息' : 'No birth chart configured'}
                  </p>
                </div>
              )}

              {/* Logout */}
              <div className="px-2 py-2">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors text-sm font-medium"
                >
                  <LogOut className="w-4 h-4 flex-shrink-0" />
                  <span>{nav.logout}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
